import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PrinterIcon } from "@heroicons/react/24/outline";
import { Link, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { IndeterminateBar, Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userCanSeePrices } from "@/lib/auth";
import {
  copyArtikel,
  listArtikel,
  type ArtikelColumnFilters,
  type ArtikelRow,
} from "@/lib/artikel";
import {
  ARTIKEL_ETIKETT_MAX,
  resolveArtikelEtikettItems,
  type ArtikelEtikettItem,
} from "@/lib/artikelEtikett";
import {
  artikelFromRowForEtikettCart,
  useArtikelEtikettCart,
} from "@/lib/artikelEtikettCart";
import { toast } from "@/lib/toast";
import { ArtikelBulkEditDialog, type ArtikelBulkSelection } from "@/pages/artikel/ArtikelBulkEditDialog";
import { ArtikelEtikettDialog } from "@/pages/artikel/ArtikelEtikettDialog";
import { ArtikelColumnsDialog } from "@/pages/artikel/ArtikelColumnsDialog";
import { ArtikelCreateDialog } from "@/pages/artikel/ArtikelCreateDialog";
import { ArtikelDetailDialog } from "@/pages/artikel/ArtikelDetailDialog";
import { ArtikelEditDialog } from "@/pages/artikel/ArtikelEditDialog";
import { GeschaeftspartnerInfoModal } from "@/pages/kontakte/GeschaeftspartnerInfoModal";
import { ArtikelImportDialog } from "@/pages/artikel/ArtikelImportDialog";
import { ArtikelTable, type ArtikelSelectionMode } from "@/pages/artikel/ArtikelTable";
import { clampArtikelColumnWidth, isArtikelPriceColumn } from "@/pages/artikel/artikelColumns";
import {
  ARTIKEL_PAGE_SIZE_OPTIONS,
  getVisibleArtikelColumns,
  getVisibleArtikelWidths,
  useArtikelPageSize,
  useArtikelTableSettings,
  useMediaQuery,
} from "@/pages/artikel/useArtikelTableSettings";
import { NativeSelect } from "@/components/ui/NativeSelect";
import { SearchInput } from "@/pages/projekte/SearchInput";
import { isArticleCodeQuery } from "@/lib/searchQuery";
import { TableSortResetBar, useTableSort } from "@/components/dataTable";

const FILTER_DEBOUNCE_MS = 300;

function formatCount(total: number): string {
  if (total === 1) return "1 Artikel";
  return `${total.toLocaleString("de-DE")} Artikel`;
}

function formatCountLabel(total: number): string {
  return `· ${formatCount(total)}`;
}

function columnFiltersToApi(raw: Record<string, string>): ArtikelColumnFilters {
  const out: ArtikelColumnFilters = {};
  for (const [key, value] of Object.entries(raw)) {
    const v = value.trim();
    if (!v) continue;
    (out as Record<string, string>)[key] = v;
  }
  return out;
}

export function ArtikelIndexPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sort, toggle, reset: resetSort } = useTableSort();
  const { user } = useCurrentUser();
  const canSeePrices = userCanSeePrices(user);
  const [rows, setRows] = useState<ArtikelRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [debouncedFilters, setDebouncedFilters] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [detailArtikelId, setDetailArtikelId] = useState<number | null>(null);
  const [detailReloadToken, setDetailReloadToken] = useState(0);
  const [editArtikelId, setEditArtikelId] = useState<number | null>(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkIntent, setBulkIntent] = useState<"edit" | "delete">("edit");
  const [etikettOpen, setEtikettOpen] = useState(false);
  const [etikettItems, setEtikettItems] = useState<ArtikelEtikettItem[]>([]);
  const [etikettLoading, setEtikettLoading] = useState(false);
  const [etikettError, setEtikettError] = useState<string | null>(null);
  const [etikettTruncated, setEtikettTruncated] = useState(false);
  const [etikettTotal, setEtikettTotal] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const etikettCart = useArtikelEtikettCart();
  const [selectionMode, setSelectionMode] = useState<ArtikelSelectionMode>("ids");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
  const [lieferantModalId, setLieferantModalId] = useState<number | null>(null);
  const [copyingArtikelId, setCopyingArtikelId] = useState<number | null>(null);
  const copyingRef = useRef(false);
  const { settings, saveSettings } = useArtikelTableSettings();
  const { pageSize, setPageSize } = useArtikelPageSize();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const requestSeqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const filterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visibleColumns = useMemo(() => {
    const cols = getVisibleArtikelColumns(settings, isMobile);
    if (canSeePrices) return cols;
    return cols.filter((col) => !isArtikelPriceColumn(col));
  }, [settings, isMobile, canSeePrices]);

  const visibleWidths = useMemo(
    () => getVisibleArtikelWidths(settings, isMobile),
    [settings, isMobile],
  );

  const apiColumnFilters = useMemo(
    () => columnFiltersToApi(debouncedFilters),
    [debouncedFilters],
  );

  const reload = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    abortRef.current?.abort();
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await listArtikel({
        q: search.trim() || undefined,
        includeDeleted,
        limit: pageSize,
        offset,
        sort: sort?.key,
        direction: sort?.direction,
        columnFilters: apiColumnFilters,
        signal: controller?.signal,
      });
      if (seq !== requestSeqRef.current) return;
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(err instanceof ApiError ? err.message : "Artikel konnten nicht geladen werden.");
      setRows([]);
      setTotal(0);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [search, includeDeleted, offset, sort, apiColumnFilters, pageSize]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(() => {
      setDebouncedFilters((prev) => {
        const prevKey = JSON.stringify(prev);
        const nextKey = JSON.stringify(columnFilters);
        if (prevKey === nextKey) return prev;
        setOffset(0);
        return columnFilters;
      });
    }, FILTER_DEBOUNCE_MS);
    return () => {
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    };
  }, [columnFilters]);

  const clearSelection = useCallback(() => {
    setSelectionMode("ids");
    setSelectedIds(new Set());
    setExcludedIds(new Set());
  }, []);

  // Auswahl bei Filter/Suche zurücksetzen – nicht bei Pagination/Sort.
  useEffect(() => {
    clearSelection();
  }, [search, includeDeleted, debouncedFilters, clearSelection]);

  useEffect(() => {
    const idParam = searchParams.get("id");
    if (!idParam) return;
    const id = Number(idParam);
    if (!Number.isFinite(id) || id <= 0) return;
    setDetailArtikelId(id);
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleCopyArtikel = useCallback(
    async (row: ArtikelRow) => {
      if (copyingRef.current) return;
      copyingRef.current = true;
      setCopyingArtikelId(row.id);
      try {
        const created = await copyArtikel(row.id);
        toast.success(
          created.hero_nr
            ? `Artikel als „${created.hero_nr}“ kopiert. Passen Sie an, was ihn vom Original unterscheidet.`
            : "Artikel kopiert. Passen Sie an, was ihn vom Original unterscheidet.",
        );
        setEditArtikelId(created.id);
        void reload();
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "Artikel konnte nicht kopiert werden.");
      } finally {
        copyingRef.current = false;
        setCopyingArtikelId(null);
      }
    },
    [reload],
  );

  const handleSearchChange = useCallback((next: string) => {
    setSearch(next);
    setOffset(0);
  }, []);

  function handleIncludeDeletedChange(checked: boolean) {
    setIncludeDeleted(checked);
    setOffset(0);
  }

  function handlePageSizeChange(raw: string) {
    const next = Number(raw);
    if (!Number.isFinite(next) || next === pageSize) return;
    setPageSize(next);
    setOffset(0);
  }

  const handleSort = useCallback(
    (key: string) => {
      toggle(key);
      setOffset(0);
    },
    [toggle],
  );

  const handleResetSort = useCallback(() => {
    resetSort();
    setOffset(0);
  }, [resetSort]);

  function handleColumnFilterChange(param: string, value: string) {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (!value.trim()) delete next[param];
      else next[param] = value;
      return next;
    });
  }

  function toggleSelected(id: number) {
    if (selectionMode === "allMatching") {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllFiltered() {
    if (selectionMode === "allMatching") {
      clearSelection();
      return;
    }
    if (total <= 0) return;
    setSelectionMode("allMatching");
    setSelectedIds(new Set());
    setExcludedIds(new Set());
  }

  const selectedCount = useMemo(() => {
    if (selectionMode === "allMatching") {
      return Math.max(0, total - excludedIds.size);
    }
    return selectedIds.size;
  }, [selectionMode, total, excludedIds, selectedIds]);

  const bulkSelection: ArtikelBulkSelection = useMemo(
    () => ({
      mode: selectionMode,
      ids: [...selectedIds],
      excludeIds: [...excludedIds],
      total: selectedCount,
      filter: {
        q: search.trim() || undefined,
        include_deleted: includeDeleted || undefined,
        ...apiColumnFilters,
      },
    }),
    [selectionMode, selectedIds, excludedIds, selectedCount, search, includeDeleted, apiColumnFilters],
  );

  const page = Math.floor(offset / pageSize) + 1;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const rangeFrom = total === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + rows.length, total);

  const countLabel = useMemo(() => {
    if (loading && rows.length === 0) return "· Lade …";
    if (loading) return "· Suche …";
    return formatCountLabel(total);
  }, [loading, rows.length, total]);

  const subtitle = useMemo(() => {
    if (loading && rows.length === 0) return "Lade Artikel …";
    if (loading) return "Suche Artikel …";
    if (total === 0) return "Keine Artikel gefunden";
    if (total <= pageSize) return formatCount(total);
    return `${formatCount(total)} · ${rangeFrom.toLocaleString("de-DE")}–${rangeTo.toLocaleString("de-DE")}`;
  }, [loading, rows.length, total, pageSize, rangeFrom, rangeTo]);

  function toggleEtikettCartRow(row: ArtikelRow) {
    const action = etikettCart.toggle(artikelFromRowForEtikettCart(row));
    if (action === "full") {
      toast.error(`Der Etikettenkorb ist voll (max. ${ARTIKEL_ETIKETT_MAX}).`);
    }
  }

  function openCartEtiketten() {
    if (etikettCart.count === 0) return;
    setEtikettItems(etikettCart.items);
    setEtikettTruncated(false);
    setEtikettTotal(etikettCart.count);
    setEtikettError(null);
    setEtikettLoading(false);
    setEtikettOpen(true);
  }

  async function addSelectionToCart() {
    if (selectedCount === 0) return;
    setAddingToCart(true);
    try {
      const result = await resolveArtikelEtikettItems({
        mode: selectionMode,
        ids: [...selectedIds],
        excludeIds: [...excludedIds],
        knownRows: rows,
        listParams: {
          q: search.trim() || undefined,
          includeDeleted,
          columnFilters: apiColumnFilters,
        },
      });
      const merged = etikettCart.addMany(result.items);
      if (merged.added === 0 && merged.skipped === 0) {
        toast.info("Die Auswahl ist bereits im Etikettenkorb.");
      } else if (merged.skipped > 0) {
        toast.info(
          `${merged.added.toLocaleString("de-DE")} gelegt, ${merged.skipped.toLocaleString("de-DE")} übersprungen (Korb max. ${ARTIKEL_ETIKETT_MAX}).`,
        );
      } else {
        toast.success(
          merged.added === 1
            ? "1 Artikel in den Etikettenkorb gelegt."
            : `${merged.added.toLocaleString("de-DE")} Artikel in den Etikettenkorb gelegt.`,
        );
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Auswahl konnte nicht in den Etikettenkorb gelegt werden.",
      );
    } finally {
      setAddingToCart(false);
    }
  }

  function handleColumnWidthChange(key: string, width: number) {
    const viewKey = isMobile ? "mobile" : "desktop";
    const currentView = settings[viewKey];
    const nextWidths = { ...(currentView?.widths ?? {}), [key]: clampArtikelColumnWidth(width) };
    void saveSettings({
      ...settings,
      [viewKey]: {
        columns: currentView?.columns?.length ? currentView.columns : visibleColumns,
        widths: nextWidths,
      },
    });
  }

  return (
    <AppShell
      title="Artikel"
      subtitle={subtitle}
      actions={
        <Link
          to="/bestellwesen/artikel/einstellungen"
          className="inline-flex items-center rounded-lg border border-[var(--eh-color-border)] px-3 py-2 text-sm font-medium text-label hover:bg-[var(--eh-color-surface-muted)]"
        >
          Einstellungen
        </Link>
      }
    >
      <div className="space-y-4">
        <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5 md:py-4">
          <div className="space-y-3 md:space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-5">
              <div className="min-w-0 space-y-1">
                <h2 className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-lg font-semibold leading-snug tracking-tight text-label md:text-xl">
                  <span>Artikel</span>
                  <span
                    className="shrink-0 whitespace-nowrap text-sm font-medium tabular-nums text-label-secondary md:text-[15px]"
                    aria-live="polite"
                  >
                    {countLabel}
                  </span>
                </h2>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-label-secondary">
                  <input
                    type="checkbox"
                    checked={includeDeleted}
                    onChange={(e) => handleIncludeDeletedChange(e.target.checked)}
                    className="rounded border-separator"
                  />
                  Gelöschte anzeigen
                </label>
                <label className="flex items-center gap-2 text-sm text-label-secondary">
                  Zeilen pro Seite
                  <NativeSelect
                    selectSize="sm"
                    aria-label="Zeilen pro Seite"
                    value={String(pageSize)}
                    wrapperClassName="min-w-[4.5rem]"
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                  >
                    {ARTIKEL_PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </NativeSelect>
                </label>
                <Button type="button" variant="secondary" onClick={() => setColumnsDialogOpen(true)}>
                  Spalten
                </Button>
                <Button type="button" onClick={() => setCreateDialogOpen(true)}>
                  Artikel anlegen
                </Button>
                <Button type="button" variant="secondary" onClick={() => setImportDialogOpen(true)}>
                  Import
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={selectedCount === 0}
                  onClick={() => {
                    setBulkIntent("edit");
                    setBulkEditOpen(true);
                  }}
                >
                  Massenbearbeitung ({selectedCount.toLocaleString("de-DE")})
                </Button>
                {canSeePrices ? (
                  <Button
                    type="button"
                    variant="danger"
                    disabled={selectedCount === 0}
                    onClick={() => {
                      setBulkIntent("delete");
                      setBulkEditOpen(true);
                    }}
                  >
                    Löschen ({selectedCount.toLocaleString("de-DE")})
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  disabled={selectedCount === 0}
                  loading={addingToCart}
                  onClick={() => void addSelectionToCart()}
                >
                  Auswahl in den Korb
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={etikettCart.count === 0}
                  leftIcon={<PrinterIcon className="h-4 w-4" />}
                  onClick={openCartEtiketten}
                >
                  Etikettenkorb ({etikettCart.count.toLocaleString("de-DE")})
                </Button>
              </div>
            </div>
            <SearchInput
              value={search}
              onChange={handleSearchChange}
              placeholder="Artikel suchen (Name, Nr., EAN, Hersteller, Lieferant) …"
              className="w-full min-w-0"
              loading={loading}
              immediateQuery={isArticleCodeQuery}
            />
          </div>
          {loading ? (
            <IndeterminateBar className="mt-3" label="Artikel werden gesucht" />
          ) : null}
          {selectionMode === "allMatching" ? (
            <p className="mt-3 text-sm text-label-secondary">
              Alle {total.toLocaleString("de-DE")} gefilterten Artikel sind ausgewählt
              {excludedIds.size > 0
                ? ` (−${excludedIds.size.toLocaleString("de-DE")} abgewählt)`
                : ""}
              .
            </p>
          ) : null}
          {etikettCart.count > 0 ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-ios bg-accent/10 px-3 py-2 text-sm text-label">
              <p>
                {etikettCart.count === 1
                  ? "1 Artikel im Etikettenkorb"
                  : `${etikettCart.count.toLocaleString("de-DE")} Artikel im Etikettenkorb`}
                . Neue Suche oder Filter löschen den Korb nicht.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={etikettCart.clear}>
                  Korb leeren
                </Button>
                <Button type="button" size="sm" onClick={openCartEtiketten}>
                  Korb drucken
                </Button>
              </div>
            </div>
          ) : null}
        </section>

        {error && <Alert variant="error">{error}</Alert>}

        {loading && rows.length === 0 ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <>
            <TableSortResetBar visible={sort != null} onReset={handleResetSort} />
            <div className={loading && rows.length > 0 ? "opacity-60 transition-opacity" : "transition-opacity"}>
              <ArtikelTable
                rows={rows}
                columns={visibleColumns}
                widths={visibleWidths}
                loading={loading}
                selectable
                selectionMode={selectionMode}
                selectedIds={selectedIds}
                excludedIds={excludedIds}
                onToggleSelect={toggleSelected}
                onToggleSelectAll={toggleSelectAllFiltered}
                columnFilters={columnFilters}
                onColumnFilterChange={handleColumnFilterChange}
                onColumnWidthChange={handleColumnWidthChange}
                onRowClick={(row) => setDetailArtikelId(row.id)}
                onLieferantClick={(id) => setLieferantModalId(id)}
                sort={sort}
                onSort={handleSort}
                etikettCart={{
                  ids: etikettCart.ids,
                  onToggle: toggleEtikettCartRow,
                }}
                onCopyArtikel={(row) => void handleCopyArtikel(row)}
                copyingArtikelId={copyingArtikelId}
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-label-secondary">
                Seite {page} von {pageCount}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-label-secondary">
                  Zeilen pro Seite
                  <NativeSelect
                    selectSize="sm"
                    aria-label="Zeilen pro Seite"
                    value={String(pageSize)}
                    wrapperClassName="min-w-[4.5rem]"
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                  >
                    {ARTIKEL_PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </NativeSelect>
                </label>
                {pageCount > 1 ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={offset <= 0 || loading}
                      onClick={() => setOffset((prev) => Math.max(0, prev - pageSize))}
                    >
                      Zurück
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={offset + pageSize >= total || loading}
                      onClick={() => setOffset((prev) => prev + pageSize)}
                    >
                      Weiter
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>

      <ArtikelColumnsDialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        settings={settings}
        onSave={saveSettings}
      />

      <ArtikelCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={(id) => {
          void reload();
          setDetailArtikelId(id);
        }}
      />

      <ArtikelImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImported={() => void reload()}
      />

      <ArtikelDetailDialog
        open={detailArtikelId != null}
        artikelId={detailArtikelId}
        reloadToken={detailReloadToken}
        onClose={() => setDetailArtikelId(null)}
        onChanged={() => void reload()}
        onEdit={(id) => {
          setEditArtikelId(id);
        }}
        onLieferantClick={(id) => setLieferantModalId(id)}
      />

      <ArtikelEditDialog
        open={editArtikelId != null}
        artikelId={editArtikelId}
        onClose={() => setEditArtikelId(null)}
        onSaved={() => {
          void reload();
          if (detailArtikelId != null && editArtikelId === detailArtikelId) {
            setDetailReloadToken((t) => t + 1);
          }
        }}
      />

      <ArtikelBulkEditDialog
        open={bulkEditOpen}
        intent={bulkIntent}
        selection={bulkSelection}
        onClose={() => setBulkEditOpen(false)}
        onSaved={() => {
          clearSelection();
          void reload();
        }}
      />

      <ArtikelEtikettDialog
        open={etikettOpen}
        onClose={() => setEtikettOpen(false)}
        items={etikettItems}
        loading={etikettLoading}
        truncated={etikettTruncated}
        total={etikettTotal}
        error={etikettError}
        onRemoveItem={(id) => {
          etikettCart.remove(id);
          setEtikettItems((prev) => prev.filter((item) => item.id !== id));
          setEtikettTotal((prev) => Math.max(0, prev - 1));
        }}
      />

      <GeschaeftspartnerInfoModal
        open={lieferantModalId != null}
        geschaeftspartnerId={lieferantModalId}
        typ="lieferant"
        onClose={() => setLieferantModalId(null)}
      />
    </AppShell>
  );
}
